import axios from 'axios';
import { getItem, setItem } from '@utils/localStorage';
import { isStaff } from './getRole';
import { staffJwtReissueService } from '@services/public/staff/jwt-reissue.service';
import { jwtReissueService } from '@services/public/admin/jwt-reissue.service';
import { displayMessage, is200xRange } from './utilities';
import { toast } from 'react-toastify';
import ENDPOINTS from './apiEndpoints';

const baseURL = window.location.origin + '/api/v1';

let logoutFn: (errorCode?: any, msg?: any) => void | undefined;
// Setter function to dynamically inject the logout function from useAuth()
export const setLogoutFunction = (
  logoutCallback: (errorCode?: any, msg?: any) => void,
) => {
  logoutFn = logoutCallback;
};

export const axiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 1000 * 60 * 5, // 5 min
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use(
  (request) => {
    const isRefreshToken = request?.url?.split('/').pop() === 'reissue';
    const token = isRefreshToken
      ? getItem('api_refresh_token')
      : getItem('api_access_token');

    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }

    return request;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const setReissueToken = async () => {
  const accessTokenExpiry = parseInt(getItem('accessTokenExpirationTime'), 10);
  if (accessTokenExpiry) {
    const currentTime = new Date().getTime();
    const timeUntilExpiry = accessTokenExpiry - currentTime;

    if (timeUntilExpiry < 0) {
      const jwtReissue = isStaff ? staffJwtReissueService : jwtReissueService;
      const { response, statusCode } = await jwtReissue();
      const code = response?.code;
      const jwtReissueInfo = response?.result?.jwtReissueInfo;
      if (is200xRange(statusCode)) {
        if (code === '1') {
          axios.defaults.headers.common['Authorization'] =
            `${jwtReissueInfo.grantType} ${jwtReissueInfo.accessToken}`;
          setItem('api_access_token', jwtReissueInfo.accessToken);
          setItem(
            'accessTokenExpirationTime',
            new Date().getTime() + jwtReissueInfo.accessTokenExpirationTime,
          );
          setReissueToken();
        }
        // else {
        //   return logoutFn(statusCode);
        // }
      }
      // else {
      //   return logoutFn(statusCode);
      // }
    }
  }
};

const showToast = ({
  response,
  isError,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: any;
  isError: boolean;
}) => {
  const url = response.config.url;

  // Regular expression to check if the last part is an ID (alphanumeric and long)
  const hasIdInUrl = (url) => /\/[a-zA-Z0-9]{10,}$/.test(url);

  // Remove query params
  const baseUrl = url?.split('?')[0];

  // Conditionally replace the last part if it's an ID
  const apiCalled =
    baseUrl && hasIdInUrl(baseUrl)
      ? baseUrl.replace(/\/[a-zA-Z0-9]{10,}$/, '/{id}')
      : baseUrl;

  const method = response.config.method?.toUpperCase();
  const code = response?.data?.code || response.response?.data?.code;
  const msg =
    displayMessage(apiCalled, method, code) ||
    response?.data?.devMsg ||
    response?.response?.data?.devMsg;

  // console.log({ url, apiCalled, method, code, msg });

  if (isError) {
    msg &&
      toast.error(msg, {
        position: 'top-right',
      });
  } else {
    msg &&
      toast.success(msg, {
        position: 'top-right',
      });
  }

  return msg;
};

// const LOGOUT_CODES = ['15230', '15300', '15310', '30010', '33190'];

axiosInstance.interceptors.response.use(
  async (response) => {
    if (response.data.code === '1') {
      showToast({ response, isError: false });
    } else {
      showToast({ response, isError: true });
    }
    return response;
  },
  async (error) => {
    error?.response?.status !== 401 &&
      window.location.pathname !== '/' &&
      showToast({ response: error, isError: true });

    const originalRequest = error.config;

    if (
      originalRequest?.url === '/admin/jwt/reissue' &&
      error?.response?.status === 401
    ) {
      return logoutFn(error.response?.data?.code);
    }

    // if (window.location.pathname !== '/') {
    //   LOGOUT_CODES.includes(error?.response?.data?.code) &&
    //     logoutFn(error?.response?.data?.code);
    // }

    if (
      error.response?.data.code !== 0 &&
      originalRequest?.url !== ENDPOINTS.ADMIN.PASSWORD.VALIDATION_CODE_ISSUE &&
      originalRequest?.url !== ENDPOINTS.ADMIN.SYSTEM.PREFERENCE.READ &&
      originalRequest?.url !== ENDPOINTS.ADMIN.EXTERNAL_DELIVERY.UPLOAD.INITIATE &&
      error?.response?.status !== 401 &&
      window.location.pathname !== '/'
    ) {
      const msg = showToast({ response: error.response, isError: true });
      logoutFn('', msg);
    }

    if(originalRequest?.url === ENDPOINTS.ADMIN.SYSTEM.PREFERENCE.READ) {
      return Promise.reject(error);
    }

    if(originalRequest?.url === ENDPOINTS.ADMIN.EXTERNAL_DELIVERY.UPLOAD.INITIATE) {
      return Promise.reject(error);
    }

    switch (error?.response?.status) {
      case 401: {
        if (window.location.pathname !== '/') {
          if (originalRequest?.url === '/admin/jwt/reissue') {
            return logoutFn(error.response?.data?.code);
          } else if (
            originalRequest?.url ===
              ENDPOINTS.ADMIN.CUSTOMER_DELIVERY.UPLOAD.URL_ASSIGN ||
            originalRequest?.url ===
              ENDPOINTS.ADMIN.CUSTOMER_DELIVERY.UPLOAD.INSERT ||
            originalRequest?.url ===
              ENDPOINTS.ADMIN.EXTERNAL_DELIVERY.UPLOAD.URL_ASSIGN ||
            originalRequest?.url ===
              ENDPOINTS.ADMIN.EXTERNAL_DELIVERY.UPLOAD.INSERT ||
            originalRequest?.url ===
              ENDPOINTS.STAFF.CUSTOMER_DELIVERY.UPLOAD.URL_ASSIGN ||
            originalRequest?.url ===
              ENDPOINTS.STAFF.CUSTOMER_DELIVERY.UPLOAD.INSERT
          ) {
            await setReissueToken();
            return axiosInstance(originalRequest);
          } else if (!originalRequest._retry) {
            originalRequest._retry = true;
            await setReissueToken();
            return axiosInstance(originalRequest);
          }
        }
        return Promise.reject(error);
      }

      case 403: {
        return Promise.reject(error);
      }

      case 400: {
        return Promise.reject(error);
      }

      case 404: {
        return Promise.reject(error);
      }

      case 500: {
        return Promise.reject(error);
      }

      case 502: {
        return Promise.reject(error);
      }

      default: {
        return Promise.reject(error);
      }
    }
  },
);
